import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Download, AlertCircle, CheckCircle, Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Upload Events admin tab (iter 106).
 *
 * Shows the funnel (attempts → file selected → initiated → completed / failed)
 * plus a raw event log. Every event is a row in db.upload_events, produced by
 * the browser during each stage of the upload flow.
 *
 * The three "rate" numbers up top are the KPIs the founder actually cares
 * about: completion_rate (what % of started attempts finished), failure_rate
 * (what % errored out), and stuck_rate (what % never reached a terminal
 * event within 30 min — those trigger admin email alerts too).
 */
export const UploadEventsTab = ({ token }) => {
  const [funnel, setFunnel] = useState(null);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [stageFilter, setStageFilter] = useState('');
  const [userQuery, setUserQuery] = useState('');

  const fetchFunnel = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/upload-events/funnel?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setFunnel(await res.json());
    } catch (e) {
      toast.error(`Funnel load failed: ${e.message}`);
    }
  }, [days, token]);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days), limit: '200' });
      if (stageFilter) params.set('stage', stageFilter);
      if (userQuery.trim()) params.set('user_query', userQuery.trim());
      const res = await fetch(`${API}/api/admin/upload-events?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEvents(data.events || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(`Event load failed: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [days, stageFilter, userQuery, token]);

  useEffect(() => {
    fetchFunnel();
    fetchEvents();
  }, [fetchFunnel, fetchEvents]);

  const handleExport = async () => {
    try {
      const res = await fetch(`${API}/api/admin/upload-events/export.csv?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audivue_upload_events_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Events exported.');
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    }
  };

  const handleSweepStuck = async () => {
    try {
      const res = await fetch(`${API}/api/admin/system/sweep-stuck-uploads`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      toast.success(
        data.stuck_sessions_notified > 0
          ? `Sent ${data.stuck_sessions_notified} stuck-upload alert(s).`
          : 'No stuck uploads found — everyone made it to a terminal state.',
      );
      fetchFunnel();
    } catch (e) {
      toast.error(`Sweep failed: ${e.message}`);
    }
  };

  const fmtPct = (n) => `${((n || 0) * 100).toFixed(1)}%`;
  const stageBadge = (s) => {
    const map = {
      attempt_started: 'bg-zinc-700 text-zinc-200',
      file_selected: 'bg-blue-900/50 text-blue-200 border border-blue-800',
      upload_initiated: 'bg-amber-900/50 text-amber-200 border border-amber-800',
      upload_completed: 'bg-emerald-900/50 text-emerald-200 border border-emerald-800',
      upload_failed: 'bg-red-900/50 text-red-200 border border-red-800',
    };
    return `px-2 py-0.5 rounded text-[11px] font-mono ${map[s] || 'bg-zinc-800 text-zinc-300'}`;
  };
  const fmtTs = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="space-y-6" data-testid="upload-events-tab">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white"
          data-testid="upload-events-days"
        >
          <option value={1}>Last 24 hours</option>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className="bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white"
          data-testid="upload-events-stage-filter"
        >
          <option value="">All stages</option>
          <option value="attempt_started">Attempt started</option>
          <option value="file_selected">File selected</option>
          <option value="upload_initiated">Upload initiated</option>
          <option value="upload_completed">Upload completed</option>
          <option value="upload_failed">Upload failed</option>
        </select>
        <input
          type="text"
          value={userQuery}
          onChange={(e) => setUserQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchEvents()}
          placeholder="Filter by email or username…"
          className="flex-1 min-w-[220px] bg-zinc-900 border border-zinc-800 rounded-md px-3 py-2 text-sm text-white placeholder-zinc-500"
          data-testid="upload-events-user-query"
        />
        <button
          onClick={() => { fetchFunnel(); fetchEvents(); }}
          className="p-2 rounded-md bg-zinc-800 hover:bg-zinc-700"
          title="Refresh"
          data-testid="upload-events-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-3 py-2 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium"
          data-testid="upload-events-export"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
        <button
          onClick={handleSweepStuck}
          className="flex items-center gap-2 px-3 py-2 rounded-md bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-sm font-medium"
          title="Manually scan for stuck uploads and email admin (also runs every 5 min automatically)"
          data-testid="upload-events-sweep"
        >
          <AlertCircle className="w-4 h-4" /> Sweep stuck now
        </button>
      </div>

      {/* Funnel KPIs */}
      {funnel && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <KpiCard
            icon={CheckCircle}
            label="Completion rate"
            value={fmtPct(funnel.rates?.completion_rate)}
            sub={`${funnel.counts?.upload_completed || 0} of ${funnel.counts?.attempts || 0} attempts`}
            accent="text-emerald-400"
            testid="kpi-completion"
          />
          <KpiCard
            icon={AlertCircle}
            label="Failure rate"
            value={fmtPct(funnel.rates?.failure_rate)}
            sub={`${funnel.counts?.upload_failed || 0} failed`}
            accent="text-red-400"
            testid="kpi-failure"
          />
          <KpiCard
            icon={Clock}
            label="Stuck / abandoned"
            value={fmtPct(funnel.rates?.stuck_rate)}
            sub={`${funnel.counts?.stuck_no_terminal_event || 0} never finished`}
            accent="text-amber-400"
            testid="kpi-stuck"
          />
        </div>
      )}

      {/* Funnel breakdown */}
      {funnel && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4" data-testid="upload-events-funnel">
          <div className="text-[11px] uppercase tracking-wide text-zinc-500 mb-3">
            Funnel — last {funnel.days} days (deduped by session)
          </div>
          <div className="space-y-2">
            <FunnelBar label="Attempts started" n={funnel.counts?.attempts} max={funnel.counts?.attempts} color="bg-zinc-500" />
            <FunnelBar label="File selected" n={funnel.counts?.file_selected} max={funnel.counts?.attempts} color="bg-blue-500" />
            <FunnelBar label="Upload initiated" n={funnel.counts?.upload_initiated} max={funnel.counts?.attempts} color="bg-amber-500" />
            <FunnelBar label="Upload completed" n={funnel.counts?.upload_completed} max={funnel.counts?.attempts} color="bg-emerald-500" />
            <FunnelBar label="Upload failed" n={funnel.counts?.upload_failed} max={funnel.counts?.attempts} color="bg-red-500" />
          </div>
        </div>
      )}

      {/* Raw event log */}
      <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div className="text-sm font-medium text-white">Recent events</div>
          <div className="text-xs text-zinc-500">{total} total · showing {events.length}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-900/60 text-[11px] uppercase tracking-wide text-zinc-500">
              <tr>
                <th className="px-4 py-2 text-left">When</th>
                <th className="px-4 py-2 text-left">User</th>
                <th className="px-4 py-2 text-left">Stage</th>
                <th className="px-4 py-2 text-left">File / Detail</th>
                <th className="px-4 py-2 text-left">Session</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} className="text-center py-10 text-zinc-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              ) : events.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-zinc-500">No events match those filters.</td></tr>
              ) : events.map((e) => (
                <tr key={e.id} className="border-t border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`upload-event-${e.id}`}>
                  <td className="px-4 py-2 text-zinc-300 whitespace-nowrap text-xs">{fmtTs(e.timestamp)}</td>
                  <td className="px-4 py-2 text-zinc-200">
                    <div className="font-medium">{e.username || '—'}</div>
                    <div className="text-[11px] text-zinc-500">{e.email || ''}</div>
                  </td>
                  <td className="px-4 py-2"><span className={stageBadge(e.stage)}>{e.stage}</span></td>
                  <td className="px-4 py-2 text-zinc-300 text-xs">
                    {e.file_metadata?.name && (
                      <div>
                        <span className="text-zinc-200">{e.file_metadata.name}</span>
                        {e.file_metadata.size ? <span className="text-zinc-500"> · {(e.file_metadata.size / (1024*1024)).toFixed(1)} MB</span> : null}
                      </div>
                    )}
                    {e.error_type && <div className="text-red-300"><span className="font-mono">[{e.error_type}]</span> {e.error_message}</div>}
                    {e.video_id && <div className="text-emerald-300 text-[11px]">→ video {e.video_id.slice(0, 8)}…</div>}
                  </td>
                  <td className="px-4 py-2 text-zinc-500 font-mono text-[11px]">{e.session_id?.slice(0, 8)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ icon: Icon, label, value, sub, accent, testid }) => (
  <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-4" data-testid={testid}>
    <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wide">
      <Icon className={`w-4 h-4 ${accent}`} />
      {label}
    </div>
    <div className={`text-3xl font-bold mt-2 ${accent}`}>{value}</div>
    <div className="text-xs text-zinc-500 mt-1">{sub}</div>
  </div>
);

const FunnelBar = ({ label, n, max, color }) => {
  const pct = max > 0 ? Math.max(2, (n / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3">
      <div className="w-40 text-xs text-zinc-400">{label}</div>
      <div className="flex-1 bg-zinc-800 h-6 rounded overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="w-16 text-right text-sm text-white font-mono">{n || 0}</div>
    </div>
  );
};
